import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Company } from '../../../models/companies/company';
import { OperationResult } from '../../../models/server-result/operation-result';
import { User } from '../../../models/User/user';
import { ServerApiService } from '../../../server-api/server-api.service';
import { AppContextService } from '../../../services/app-context.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { LogService } from '../../../services/log/log.service';
import { CodeDecodeValue } from '../../../models/common/code-decode-value';
import { TimeZoneValue } from '../../../models/common/time-zone-value';
import { UtilitiesService } from '../../../services/utils/utilities.service';
import { finalize } from 'rxjs';
import { ServerErrors } from '../../../models/enums/server-errors';
import { TranslateService } from '../../../services/translate/translate.service';
import { RouteAuthorizationService } from '../../../services/authorization/route-authorization.service';
import { BreadcrumbItem } from '../../../shared-components/custom-breadcrumbs/custom-breadcrumbs.component';
import { CompanyApiService } from '../../../server-api/company-api/company-api.service';

@Component({
	selector: 'eif-company-details',
	templateUrl: './company-details.component.html',
	styleUrls: ['./company-details.component.scss'],
	standalone: false,
})
export class CompanyDetailsComponent implements OnInit {
	public company: Company;
	public loggedUser: User | null = new User();
	public isPerformingRequest = false;

	public languageCodes: CodeDecodeValue[];
	public timeZones: TimeZoneValue[];

	public breadcrumbItems: BreadcrumbItem[] = [];

	constructor(
		private serverApiService: ServerApiService,
		public appContext: AppContextService,
		private companyApiService: CompanyApiService,
		private toasterService: ToasterService,
		private route: ActivatedRoute,
		private logger: LogService,
		private utilitiesService: UtilitiesService,
		public router: Router,
		private translateService: TranslateService,
		private routeAuthorizationService: RouteAuthorizationService
	) {}

	private canAccessCompanyDetails(user: User | null): boolean {
		return (
			!!user?.IsSystemAdmin ||
			!!user?.IsSupportOperator ||
			!!user?.IsBackOfficeOperator ||
			!!user?.IsSupplier ||
			!!user?.IsBuyer ||
			!!user?.IsFinancier
		);
	}

	ngOnInit() {
		this.route.params.subscribe((params) => {
			this.appContext.getLoggedUserAsync().subscribe((userResult) => {
				this.loggedUser = userResult.Data;

				if (!this.canAccessCompanyDetails(this.loggedUser)) {
					return this.routeAuthorizationService.routeOnUnauthorized();
				}

				const companyId = params['companyId'];
				if (companyId) {
					this.getCompanyDetails(companyId);
				} else {
					return this.routeAuthorizationService.routeOnUnauthorized();
				}
			});
		});
	}

	private initializeBreadcrumbs() {
		this.breadcrumbItems = [
			{
				label: this.translateService.translate('breadcrumb.home', 'client'),
				route: ['/private/home'],
			},
			{
				label: this.translateService.translate('breadcrumb.companies', 'client'),
				route: ['/private/companies'],
			},
			{
				label: this.company?.Name,
			},
		];
	}

	private getCompanyDetails(companyId: string) {
		this.isPerformingRequest = true;

		this.companyApiService
			.getCompany(companyId)
			.pipe(
				finalize(() => {
					this.isPerformingRequest = false;
					this.initializeBreadcrumbs();
				})
			)
			.subscribe({
				next: (getCompanyOperationResult: OperationResult<Company>) => {
					if (getCompanyOperationResult.IsValid) {
						this.company = getCompanyOperationResult.Data;
					} else {
						this.processInvalidGetCompany(getCompanyOperationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.logger.error(httpErrorResponse.message);
					if (httpErrorResponse.status === 400) {
						this.routeAuthorizationService.routeOnUnauthorized();
					} else {
						this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
					}
				},
			});

		const serviceCallGetTimeZones = this.serverApiService.getTimeZones();
		const serviceCallLanguageCodes = this.serverApiService.getLanguageCodes();

		serviceCallLanguageCodes.subscribe({
			next: (operationResult: OperationResult<CodeDecodeValue[]>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidLanguageCodesResult(operationResult);
				} else {
					this.processinValidLanguageCodesResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[EditCompanyComponent::getLanguageCodes] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[EditCompanyComponent::getLanguageCodes] HttpErrorResponse: ' + httpErrorResponse.error + httpErrorResponse.message
				);
			},
		});

		serviceCallGetTimeZones.subscribe({
			next: (operationResult: OperationResult<TimeZoneValue[]>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidTimeZonesResult(operationResult);
				} else {
					this.processInvalidTimeZonesResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[EditCompanyComponent::getTimeZones] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[EditCompanyComponent::getTimeZones] HttpErrorResponse: ' + httpErrorResponse.error + httpErrorResponse.message
				);
			},
		});
	}

	private processinValidLanguageCodesResult(operationResult: OperationResult<CodeDecodeValue[]>): any {
		this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_LANGUAGE_CODES', 'list-errorCodes'));
		operationResult.Errors?.forEach((element) => {
			this.logger.error('Loading language codes Error:' + element.Code, element.Field);
		});
	}

	private processInvalidTimeZonesResult(operationResult: OperationResult<TimeZoneValue[]>): any {
		this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_TIME_ZONESRESULTFALSE', 'list-errorCodes'));
		operationResult.Errors?.forEach((element) => {
			this.logger.error('Loading time zones Error:' + element.Code, element.Field);
		});
	}

	private processValidLanguageCodesResult(operationResult: OperationResult<CodeDecodeValue[]>): any {
		this.languageCodes = operationResult.Data;
	}

	private processValidTimeZonesResult(operationResult: OperationResult<TimeZoneValue[]>): void {
		this.timeZones = this.utilitiesService.GetOrderedTimeZoneValues(operationResult.Data, false);
	}

	private processInvalidGetCompany(operationResult: OperationResult<Company>) {
		if (!!operationResult.Errors?.some((error) => error.Code === ServerErrors.Unauthorized)) {
			this.routeAuthorizationService.routeOnUnauthorized();
		} else {
			this.toasterService.showError(this.translateService.translate('COULD_NOT_RETRIEVE_COMPANYRESULTFALSE', 'list-errorCodes'));
			operationResult.Errors?.forEach((element) => {
				this.logger.error('Loading company Error:' + element.Code, element.Field);
			});
		}
	}

	public userCanEditCompanyInformation(): boolean {
		if (this.loggedUser?.IsSystemAdmin) return true;

		if (this.loggedUser?.IsSupportOperator) return true;

		if (this.loggedUser?.Companies && !!this.company) {
			return !!this.loggedUser.Companies.find((c) => c.Id === this.company.Id) && this.loggedUser.IsCompanyAdmin;
		}

		return false;
	}

	public navigateToCompanies(): void {
		this.router.navigate(['/private/companies']);
	}

	public getTimeZoneDisplayName(timeZoneCode: string): string {
		if (!timeZoneCode || !this.timeZones) return timeZoneCode || '';

		const timeZone = this.timeZones.find((tz) => tz.IanaCode === timeZoneCode);
		const untranslatedResult = timeZone ? timeZone.TimeZoneName : timeZoneCode;

		return this.translateService.translate(untranslatedResult, 'list-timeZones');
	}

	public getLanguageDisplay(languageCode: string): string {
		if (!languageCode || !this.languageCodes) return languageCode || '';

		const language = this.languageCodes.find((lc) => lc.Code === languageCode);
		return language ? `${language.Code} - ${language.Decode}` : languageCode;
	}

	public navigateToEditCompany(): void {
		this.router.navigate(['/private/company/edit', this.company?.Id]);
	}
}
